//shared containers
import { Footer } from '@/containers-m/Footer';
import { Navigation } from '/containers-m/';

//containers
import {
  Hero,
  WatchItRunYourFreight,
  HowItWorks,
  RunsOnYourStack,
  TheRestOfTheLineup,
  EndCTA,
} from '../containers';

//styles
import {
  PageWrapper,
  YardTaskOrchestrationGlobalStyle,
} from './YardTaskOrchestration.styles';

const hero = {
  slug: 'yard-task-orchestration',
  title: 'Yard Task Orchestration',
  description:
    'Gets every scheduled pickup ready before the carrier arrives. It selects the asset, schedules the inspection and preload, moves it to the dock and back to the yard, and sends the yard lead the options when something doesn’t fit.',
};

const watchItRun = {
  title: 'A drop trailer pickup is booked for Thursday',
  stageHeadTitle:
    'A drop trailer pickup for Carrier ABC is booked for Thursday at 2:00 PM.',
  stageHeadMeta: 'Yard · Carrier ABC pickup',
  rows: [
    {
      time: 'Tue 09:00',
      text: 'Pickup booked for Thursday 2:00 PM. 4 empty ABC trailers in the yard.',
    },
    {
      time: 'Tue 09:01',
      text: 'Skipped 1 marked inoperable and 1 already reserved. Reserved T-4410, in the yard 9 days.',
    },
    {
      time: 'Tue 09:02',
      text: 'Inspection task created. Preload booked on the Outbound Dock for Wednesday 10:00 AM.',
    },
    {
      time: 'Wed 09:30',
      text: 'Move to dock created for 9:30 AM. After loading, move back to the yard created.',
    },
    {
      time: 'Thu 14:00',
      text: 'T-4410 staged in the yard when the carrier arrived. Every task done in order.',
    },
  ],
  summaryText:
    'A drop trailer pickup was booked for Thursday. Loadsmart AI reserved the best empty trailer, skipping one that was inoperable and one already reserved, booked a preload slot on the Outbound Dock, created the inspection and the moves to the dock and back to the yard, and the trailer was staged when the carrier arrived.',
  agentBadge: 'Yard Agent',
  escalationTitle: 'Anything off-plan goes to a person.',
  escalationText:
    'When no eligible asset exists, an inspection fails, or no preload slot fits before the pickup, the agent stops and sends your yard lead the options. You set which docks and windows it may book, since preload slots take capacity from carriers.',
};

const howItWorks = {
  stepsTitleWhite: 'Creates the right yard task at the right time,',
  stepsTitleGradient: 'and keeps the work in sequence.',
  steps: [
    {
      title: 'Selects the asset',
      description:
        'Ranks assets by time in the yard, status and state (empty, operable), equipment type and ownership, then reserves the best match so no other pickup takes it.',
    },
    {
      title: 'Schedules the preload',
      description:
        'Books a slot on the dock schedule within the windows you allow, and creates the inspection and move-to-dock tasks, timed to your process.',
    },
    {
      title: 'Returns the loaded asset to the yard',
      description: 'Creates the move back to a yard spot once loading is complete.',
    },
    {
      title: 'Escalates',
      description:
        'If no eligible asset exists, an inspection fails, or no preload slot fits before the pickup, it stops and sends the yard lead the options.',
    },
  ],
  outcomes: [
    {
      title: 'The asset is ready at pickup',
      description:
        'Every task from selection to staging is scheduled backward from the pickup time.',
    },
    {
      title: 'Inspections happen every time',
      description:
        'An inspection is created for every asset before it moves to a dock, so none get skipped on a busy shift.',
    },
    {
      title: 'Two pickups never claim one trailer',
      description:
        'Reservations are checked before an asset is selected, so the same trailer is not promised twice.',
    },
  ],
};

const runsOnYourStackNote =
  'Lead time follows your process, not a fixed number of days. Gate and appointment data come from Opendock; asset status and ownership come from your yard or WMS records.';

const lineupCards = [
  {
    slug: 'dock-schedule-management',
    title: 'Dock Schedule Management',
    description: 'Keeps the dock schedule the yard works to.',
  },
  {
    slug: 'detention-handling',
    title: 'Detention Handling',
    description: 'Warns the dock before a trailer’s free time runs out.',
  },
  {
    slug: 'document-collection-and-filing',
    title: 'Document Collection & Filing',
    description:
      'We capture every quote, tender, rate con, BOL, POD and invoice as it arrives, and file it against the right shipment automatically.',
  },
  {
    slug: 'tracking-and-status-updates',
    title: 'Tracking & Status Updates',
    description:
      'We get status from carriers so your team stops making check calls, and push clean updates to the people who need them.',
  },
  {
    slug: 'tender-failures',
    title: 'Tender Failures',
    description:
      'When a tender falls through, we re-tender down your routing guide and re-book, before the miss ever reaches your team.',
  },
  {
    slug: 'proactive-load-audits',
    title: 'Proactive Load Audits',
    description:
      'We check every load before pickup and flag at-risk transit early, so problems surface before they turn into penalties.',
  },
  {
    slug: 'claims-processing',
    title: 'Claims Processing',
    description:
      'We gather the documentation, validate the claim, and draft the response for your approval, so claims get filed and recovered.',
  },
  {
    slug: 'scheduling-and-re-scheduling',
    title: 'Scheduling & Re-Scheduling',
    description:
      'We book dock appointments against carrier and facility availability, and when a pickup slips, we re-schedule and notify everyone who needs to know.',
  },
  {
    slug: 'purchase-order-follow-up',
    title: 'Purchase Order Follow-Up',
    description:
      'We find purchase orders due with no appointment, and get the supplier or carrier to book before stock runs short.',
  },
  {
    slug: 'warehouse-customer-service',
    title: 'Warehouse Customer Service',
    description:
      'We answer your warehouse’s status questions and book appointments from an email, using your dock schedule and WMS.',
  },
];

function YardTaskOrchestration() {
  return (
    <PageWrapper>
      <YardTaskOrchestrationGlobalStyle />
      <Navigation theme="dark" />
      <Hero {...hero} />
      <WatchItRunYourFreight {...watchItRun} />
      <HowItWorks {...howItWorks} />
      <RunsOnYourStack note={runsOnYourStackNote} />
      <TheRestOfTheLineup cards={lineupCards} />
      <EndCTA agentName="Yard Task Orchestration" />
      <Footer />
    </PageWrapper>
  );
}

export default YardTaskOrchestration;
export { YardTaskOrchestration };
